// Display-only Shopify currency context. It never quotes, edits a cart or
// authorizes a payment. The controlled checkout uses its own server-side quote.
const KIND = 'kob-sales-display-currency';
const READY = 'kob:sales:currency-ready';
const CONTEXT = 'kob:sales:currency-context';
const PRODUCT_OPS_ORIGIN = 'https://product-ops.kind-of-blu.com';
const PARENT_ORIGINS = ['https://kind-of-blu.com', 'https://www.kind-of-blu.com'];
const APPROXIMATE = { en: 'approx.', 'zh-CN': '约合', ja: '参考価格' };
const ZERO_DECIMAL_REFERENCE = new Set(['JPY', 'KRW', 'VND', 'CNY', 'TWD', 'HKD']);
const FALLBACK_CURRENCIES = new Set(['JPY', 'CNY', 'USD', 'EUR', 'GBP', 'HKD', 'TWD', 'KRW', 'AUD', 'CAD', 'SGD', 'CHF', 'NZD', 'THB', 'VND']);
let supported;
function currencyCode(value) {
  const code = String(value || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return '';
  if (!supported) {
    try { supported = new Set(Intl.supportedValuesOf('currency')); }
    catch { supported = FALLBACK_CURRENCIES; }
  }
  return supported.has(code) ? code : '';
}
const language = value => /^zh/i.test(value || '') ? 'zh-CN' : /^ja/i.test(value || '') ? 'ja' : 'en';
const validRate = value => typeof value === 'number' && Number.isFinite(value) && value > 0;
const formatter = (amount, currency, locale) => new Intl.NumberFormat(language(locale), {
  style: 'currency', currency, currencyDisplay: 'code',
  maximumFractionDigits: ZERO_DECIMAL_REFERENCE.has(currency) ? 0 : 2,
  minimumFractionDigits: 0,
}).format(amount);

export function normalizeSalesCurrencyContext(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || value.kind !== KIND ||
      value.version !== 1 || value.source !== 'shopify-storefront') return null;
  const base = currencyCode(value.base_currency), currency = currencyCode(value.currency);
  if (!base || !currency || value.rate !== null && !validRate(value.rate)) return null;
  return { kind: KIND, version: 1, source: 'shopify-storefront', base_currency: base,
    currency, rate: currency === base ? 1 : value.rate };
}

// The website's existing preference comes from Shopify localization and its
// storefront Shopify.currency object. Language is never a currency preference.
export function createSalesCurrencyContext({ shopifyCurrency = {}, baseCurrency = 'JPY',
  localizedCurrency = '' } = {}) {
  const base = currencyCode(baseCurrency);
  if (!base) return null;
  const current = shopifyCurrency && typeof shopifyCurrency === 'object' ? shopifyCurrency : {};
  const active = currencyCode(current.active || current.current || localizedCurrency || base);
  const currency = active || base;
  const rate = Number(current.rate ?? current.exchange_rate);
  return normalizeSalesCurrencyContext({ kind: KIND, version: 1, source: 'shopify-storefront',
    base_currency: base, currency, rate: currency === base ? 1 : validRate(rate) ? rate : null });
}

export function formatSalesDisplayMoney(price, context, locale = 'en') {
  const currency = currencyCode(price?.currency);
  const raw = String(price?.amount ?? '');
  const amount = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(raw) ? Number(raw) : NaN;
  if (!currency || !Number.isFinite(amount)) return { text: '—', base_text: '—',
    approximate: false, converted: false, amount: null, currency: currency || null };
  const baseText = formatter(amount, currency, locale), normalized = normalizeSalesCurrencyContext(context);
  if (!normalized || normalized.base_currency !== currency || normalized.currency === currency || !validRate(normalized.rate))
    return { text: baseText, base_text: baseText, approximate: false, converted: false, amount, currency };
  const converted = amount * normalized.rate;
  if (!Number.isFinite(converted)) return { text: baseText, base_text: baseText,
    approximate: false, converted: false, amount, currency };
  return { text: `${APPROXIMATE[language(locale)]} ${formatter(converted, normalized.currency, locale)}`,
    base_text: baseText, approximate: true, converted: true, amount: converted, currency: normalized.currency };
}

function origins(values) {
  return [...new Set(values)].filter(value => {
    try { const url = new URL(value); return url.origin === value && ['https:', 'http:'].includes(url.protocol); }
    catch { return false; }
  });
}

// Receiver validates both the embedding window and its exact permitted origin.
// An unrelated tab, injected sibling frame or URL exchange rate is ignored.
export function bindSalesCurrencyReceiver({ onChange, allowedParentOrigins = PARENT_ORIGINS,
  hostWindow = window } = {}) {
  if (typeof onChange !== 'function' || hostWindow.parent === hostWindow) return () => {};
  const trusted = origins(allowedParentOrigins), parent = hostWindow.parent;
  const receive = event => {
    if (event.source !== parent || !trusted.includes(event.origin) || event.data?.type !== CONTEXT ||
        event.data.version !== 1) return;
    const context = normalizeSalesCurrencyContext(event.data.context);
    if (context) onChange(context);
  };
  hostWindow.addEventListener('message', receive);
  for (const origin of trusted) parent.postMessage({ type: READY, version: 1 }, origin);
  return () => hostWindow.removeEventListener('message', receive);
}

export function bindSalesCurrencyBridge({ root, hostWindow = window,
  productOpsOrigin = PRODUCT_OPS_ORIGIN } = {}) {
  const frame = root?.querySelector('[data-sales-access-frame]');
  if (!frame || !origins([productOpsOrigin]).length) return () => {};
  const send = () => {
    const context = createSalesCurrencyContext({ shopifyCurrency: hostWindow.Shopify?.currency,
      baseCurrency: root.dataset.shopCurrency, localizedCurrency: root.dataset.selectedCurrency });
    if (context && frame.contentWindow) frame.contentWindow.postMessage({ type: CONTEXT, version: 1, context }, productOpsOrigin);
  };
  const receive = event => {
    if (event.source === frame.contentWindow && event.origin === productOpsOrigin &&
        event.data?.type === READY && event.data.version === 1) send();
  };
  hostWindow.addEventListener('message', receive);
  frame.addEventListener('load', send);
  send();
  return () => { hostWindow.removeEventListener('message', receive); frame.removeEventListener('load', send); };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const root = document.querySelector('[data-sales-preview-access]');
  if (root) bindSalesCurrencyBridge({ root });
}
